/** The consolidated view's own Cytoscape instance (not the Workbench GraphCanvas). */
import { memo, useEffect, useRef, useState } from 'react';
import cytoscape from 'cytoscape';
import type { ElementDefinition } from 'cytoscape';
import { roleStyle, EDGE_KIND_STYLE } from './roleStyle';
import { CyZoomControls } from '../viewport/CyZoomControls';
import { attachViewportKeys, enableViewport } from '../viewport/cyViewport';
import type { BucketRole, ConsolidatedEdgeKind } from './types';

/** Camera kept across an instance re-create, valid only for the root set it was taken under. */
export interface KeptCamera { rootKey: string; zoom: number; pan: { x: number; y: number } }
export const rootKeyOf = (rootIds: string[]) => [...rootIds].sort().join('|');

export interface GraphTarget { kind: 'bucket' | 'edge'; id: string; x: number; y: number }

interface Props {
  elements: ElementDefinition[];
  rootIds: string[];
  onHover: (t: GraphTarget | null) => void;
  onActivate: (t: GraphTarget) => void;
}

const ROLES: BucketRole[] = [
  'subject', 'depositors', 'intermediaries', 'exchange_offramp', 'funding_exchange',
  'issuer', 'identities', 'sources', 'context',
];
const KINDS = Object.keys(EDGE_KIND_STYLE) as ConsolidatedEdgeKind[];

export function buildStylesheet(): cytoscape.StylesheetJson {
  const sheet: cytoscape.StylesheetJson = [
    {
      selector: 'node',
      style: {
        label: 'data(label)', 'text-wrap': 'wrap', 'text-max-width': '150px', 'text-valign': 'bottom', 'text-margin-y': 6,
        'font-size': 11, color: '#334155', 'text-background-color': '#fff', 'text-background-opacity': 0.8,
        'text-background-padding': '2px', shape: 'round-rectangle', width: 'data(size)', height: 'data(size)', 'border-width': 2,
      },
    },
    {
      selector: 'edge',
      style: {
        label: 'data(label)', width: 'data(width)', 'font-size': 9.5, color: '#334155', 'curve-style': 'bezier',
        'target-arrow-shape': 'triangle', 'text-background-color': '#fff', 'text-background-opacity': 0.85,
        'text-background-padding': '2px', 'text-rotation': 'autorotate',
      },
    },
    { selector: ':selected', style: { 'overlay-color': '#fbbf24', 'overlay-opacity': 0.25 } },
  ];
  for (const r of ROLES) {
    const s = roleStyle(r);
    sheet.push({
      selector: `node[role = "${r}"]`,
      style: { 'background-color': s.fill, 'border-color': s.stroke, 'border-style': s.dashed ? 'dashed' : 'solid' },
    });
  }
  for (const k of KINDS) {
    const s = EDGE_KIND_STYLE[k];
    sheet.push({
      selector: `edge[kind = "${k}"]`,
      style: { 'line-color': s.color, 'target-arrow-color': s.color, 'line-style': s.dashed ? 'dashed' : 'solid' },
    });
  }
  return sheet;
}

function ConsolidatedGraphImpl({ elements, rootIds, onHover, onActivate }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const cbs = useRef({ onHover, onActivate });
  cbs.current = { onHover, onActivate };
  const [cyInst, setCyInst] = useState<cytoscape.Core | null>(null);
  // Survives the effect below re-creating the instance when `elements` change.
  const keptCamera = useRef<KeptCamera | null>(null);

  useEffect(() => {
    if (!host.current) return;
    const cy = cytoscape({
      container: host.current,
      elements,
      style: buildStylesheet(),
      layout: { name: 'preset' },
      minZoom: 0.3,
      maxZoom: 2.5,
      wheelSensitivity: 0.3,
    });
    setCyInst(cy);
    enableViewport(cy);
    const detachKeys = attachViewportKeys(cy);
    // breadthfirst grows top-down; swapping the axes makes it read left to right.
    const roots = rootIds.length ? cy.nodes().filter((n) => rootIds.includes(n.id())) : undefined;
    const rootKey = rootKeyOf(rootIds);
    const kept = keptCamera.current && keptCamera.current.rootKey === rootKey ? keptCamera.current : null;
    cy.layout({
      name: 'breadthfirst', directed: true, padding: 40, spacingFactor: 1.5, fit: !kept,
      ...(roots ? { roots } : {}),
      transform: (_n: unknown, p: { x: number; y: number }) => ({ x: p.y, y: p.x }),
    } as cytoscape.LayoutOptions).run();
    // Same root set as before: keep where the officer was looking instead of re-fitting.
    if (kept) cy.viewport({ zoom: kept.zoom, pan: kept.pan });
    keptCamera.current = null;
    const target = (ev: cytoscape.EventObject): GraphTarget => ({
      kind: ev.target.isNode() ? 'bucket' : 'edge',
      id: String(ev.target.id()),
      x: ev.renderedPosition?.x ?? 0,
      y: ev.renderedPosition?.y ?? 0,
    });
    cy.on('mouseover', 'node, edge', (ev) => cbs.current.onHover(target(ev)));
    cy.on('mouseout', 'node, edge', () => cbs.current.onHover(null));
    cy.on('tap', 'node, edge', (ev) => cbs.current.onActivate(target(ev)));
    return () => {
      keptCamera.current = { rootKey, zoom: cy.zoom(), pan: { ...cy.pan() } };
      detachKeys(); setCyInst(null); cy.destroy();
    };
  }, [elements, rootIds]);

  return (
    <>
      <div ref={host} className="absolute inset-0" data-testid="consol-graph" />
      <CyZoomControls cy={cyInst} />
    </>
  );
}

export const ConsolidatedGraph = memo(ConsolidatedGraphImpl);
